import { User, UserWithToken, WithData } from '@ygoargentina/sdk/api/api.types';
import { authService } from './auth-service';

/**
 * Login user via REST API
 * For web apps: uses cookies
 * For mobile apps (service: 'super-app'): returns accessToken in user object
 */
/**
 * Las credenciales de login. Van explícitas y no derivadas de `User`:
 * `password` es algo que se manda, no algo que el API devuelve —no está en
 * `user.graphql`— y sólo vivía en el tipo `User` para que estos `Pick`
 * funcionaran. Se exporta para que los formularios de cada app la reusen en
 * vez de rearmarla.
 */
export type LoginUserInput = {
  cossyId?: string;
  email?: string;
  password: string;
  service: string;
  version: string;
};

export const loginUser = async (
  input: LoginUserInput
): Promise<WithData<UserWithToken>> => {
  const result = await authService.login({
    email: input.email || undefined,
    cossyId: input.cossyId || undefined,
    password: input.password,
    service: input.service,
    version: input.version,
  });

  if (result.error || !result.data?.user) {
    throw new Error(result.message || 'login_failed');
  }

  return {
    data: {
      ...result.data.user,
      accessToken: result.data.user.accessToken || result.data.accessToken,
    } as UserWithToken,
  };
};

/**
 * First-time login (set password for migrated users)
 */
/** Las credenciales del primer login, que además fija la contraseña. */
export type FirstLoginInput = {
  cossyId: string;
  password: string;
  userId: number;
  service: string;
  version: string;
};

export const loginUserAndSetPassword = async (
  input: FirstLoginInput
): Promise<WithData<UserWithToken>> => {
  const result = await authService.firstLogin({
    userId: input.userId,
    cossyId: input.cossyId,
    password: input.password,
    service: input.service,
    version: input.version,
  });

  if (result.error || !result.data?.user) {
    throw new Error(result.message || 'first_login_failed');
  }

  return {
    data: {
      ...result.data.user,
      accessToken: result.data.user.accessToken || result.data.accessToken,
    } as UserWithToken,
  };
};

/**
 * Refresh user data
 * @deprecated Use authService.getCurrentUser() instead
 */
export const refreshUser = async (
  _n: number
): Promise<WithData<UserWithToken>> => {
  const user = await authService.getCurrentUser();

  if (!user) {
    throw new Error('not_authenticated');
  }

  return {
    data: user as UserWithToken,
  };
};

/**
 * Request password recovery email
 */
export const recoverPassword = async (
  input: Partial<Pick<User, 'cossyId' | 'email'>> & { source?: string }
): Promise<WithData<User>> => {
  const result = await authService.recoverPassword({
    email: input.email || undefined,
    cossyId: input.cossyId || undefined,
    source: input.source,
  });

  if (result.error || !result.data) {
    throw new Error(result.message || 'recover_failed');
  }

  return {
    data: result.data as User,
  };
};

/**
 * Validate a password reset token
 */
export const validateResetToken = async (
  hash: string
): Promise<WithData<Pick<User, 'id' | 'email'>>> => {
  const result = await authService.validateResetToken(hash);

  if (result.error || !result.data) {
    throw new Error(result.message || 'invalid_token');
  }

  return {
    data: result.data,
  };
};

/**
 * Reset password using a valid token
 */
export const resetPassword = async (
  hash: string,
  password: string
): Promise<WithData<User>> => {
  const result = await authService.resetPassword(hash, password);

  if (result.error || !result.data) {
    throw new Error(result.message || 'reset_failed');
  }

  return {
    data: { id: result.data.id } as User,
  };
};
